/**
 * ApprovalsSecondaryNav — render trong slot secondary sidebar khi group.key='approvals'.
 * Cùng layout với ApprovalShell sidebar nhưng hosted ở global slot → giữ nguyên khi navigate giữa
 * /approvals · /approvals/templates · /approvals/report · /approvals/:id
 */
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Send, Inbox, Plus, Search, Clock, CheckCircle2, XCircle, Eye, Flag, Hourglass,
  FileSpreadsheet, BarChart3, ChevronRight, Share2, Inbox as InboxIcon,
} from 'lucide-react';
import api from '@host/axios';
import { useAuth } from '@host/auth';
import ApprovalTemplatePicker from '../../approvals/ApprovalTemplatePicker';
import { stripDefaultApprovalParams } from '../../../utils/approvalUrl';

import { Trans, useLingui } from '@lingui/react/macro';
import { i18n } from '../../../i18n/runtime.js';
import { APPROVAL_TAB_LABELS, APPROVAL_FILTER_LABELS, APPROVAL_OTHER_TEMPLATE } from '../../../constants/approvalFilters';

// label = descriptor Lingui (msg``) — đọc bằng i18n._(x.label) ở chỗ render.
const TOP_TABS = [
  { key: 'tasks', label: APPROVAL_TAB_LABELS.tasks, icon: Inbox },
  { key: 'sent', label: APPROVAL_TAB_LABELS.sent, icon: Send },
];

// hideCount=true → KHÔNG hiển thị badge tổng count cạnh tên filter (theo yêu cầu UX:
// hoàn thành / được CC / kết thúc là history ko cần đập vào mặt user mỗi lần load).
const FILTERS_TASKS = [
  { key: 'pending', label: APPROVAL_FILTER_LABELS.pending, icon: Clock },
  { key: 'upcoming', label: APPROVAL_FILTER_LABELS.upcoming, icon: Hourglass, hideCount: true },
  { key: 'completed', label: APPROVAL_FILTER_LABELS.completed, icon: CheckCircle2, hideCount: true },
  { key: 'ended', label: APPROVAL_FILTER_LABELS.ended, icon: Flag, hideCount: true },
  { key: 'cc', label: APPROVAL_FILTER_LABELS.cc, icon: Eye, hideCount: true },
];

const FILTERS_SENT = [
  { key: 'active', label: APPROVAL_FILTER_LABELS.active, icon: Clock },
  { key: 'done', label: APPROVAL_FILTER_LABELS.done, icon: CheckCircle2, hideCount: true },
  { key: 'rejected', label: APPROVAL_FILTER_LABELS.rejected, icon: XCircle, hideCount: true },
];

// Badge sidebar chỉ hiển thị "99+" khi vượt 99 — số chính xác xem ở header main pane
// (khi bấm vào mục/mẫu) + phân trang. Tránh badge số lớn (147, 300, 1012...) rối mắt.
const fmtBadge = (n) => (n > 99 ? '99+' : n);

export default function ApprovalsSecondaryNav() {
  const {
    t
  } = useLingui();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  const [counts, setCounts] = useState({});
  const [showSubmit, setShowSubmit] = useState(false);
  // Link mở thẳng form gửi đơn: `/approvals?new=<mẫu>[&subject=<người>]` — bot HR nhắc quản lý gửi đơn
  // «Đánh giá & đề xuất tái ký» chọn sẵn nhân sự (ô `_kind_renewal_subject`). Đọc xong thì gỡ khỏi URL.
  const [deepLink, setDeepLink] = useState(null);
  useEffect(() => {
    const tplId = searchParams.get('new');
    if (!tplId) return;
    const subject = searchParams.get('subject');
    setDeepLink({ templateId: tplId, prefill: subject ? { _kind_renewal_subject: subject } : null });
    setShowSubmit(true);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    next.delete('subject');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [searchText, setSearchText] = useState(searchParams.get('q') || '');
  // Filter nào đang expand để show template list. null = không expand.
  const [expandedFilter, setExpandedFilter] = useState(null);
  // Template buckets per filter — { pending: [{id,name,count}], completed: [...], ... }
  const [templateBuckets, setTemplateBuckets] = useState({});

  const isInbox = location.pathname === '/approvals' || location.pathname === '/approvals/inbox';
  const isTemplates = location.pathname === '/approvals/templates';
  const isReport = location.pathname === '/approvals/report';
  const isShared = location.pathname === '/approvals/shared';
  const sharedType = searchParams.get('type') || 'received';
  const [sharedReceivedCount, setSharedReceivedCount] = useState(0);

  useEffect(() => {
    api.get('/approvals/share-lists/received-count')
      .then(r => setSharedReceivedCount(r.data?.data?.count || 0))
      .catch(() => {});
  }, [location.pathname, location.search]);

  const topTab = searchParams.get('tab') || 'tasks';
  const filter = searchParams.get('filter') || (topTab === 'sent' ? 'active' : 'pending');
  const activeTemplateId = searchParams.get('template_id');
  const sidebarFilters = topTab === 'tasks' ? FILTERS_TASKS : FILTERS_SENT;

  const loadCounts = useCallback(() => {
    api.get('/approvals/inbox-counts').then(r => setCounts(r.data?.data || {})).catch(() => {});
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts, location.pathname, location.search]);

  // Lazy load template bucket cho filter đang expand.
  // - pending/upcoming: dùng endpoint chuyên (my-pending/upcoming-for-me), list bounded nhỏ
  //   → gom client-side OK.
  // - completed/ended/cc/active/done/rejected: dùng /approvals/template-buckets — DB COUNT+GROUP BY,
  //   KHÔNG cắt cụt. Trước đây fetch 200 đơn rồi gom client → tổng mọi mẫu kẹt = 200, đơn cũ bị
  //   đẩy ra khi có đơn mới của mẫu khác → badge tụt dần ("lệnh hoàn thành ko còn ở đây").
  const groupClientSide = (list) => {
    const map = new Map();
    for (const req of list) {
      const tid = req.template_id || req.template?.id;
      const tname = req.template_name || req.template?.name || i18n._(APPROVAL_OTHER_TEMPLATE);
      if (!tid) continue;
      if (!map.has(tid)) map.set(tid, { id: tid, name: tname, count: 0 });
      map.get(tid).count++;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  };
  const loadBucketFor = useCallback(async (filterKey) => {
    if (templateBuckets[filterKey]) return; // cache hit
    try {
      let bucket;
      if (filterKey === 'pending' || filterKey === 'upcoming') {
        const url = filterKey === 'pending' ? '/approvals/my-pending' : '/approvals/upcoming-for-me';
        const r = await api.get(url);
        bucket = groupClientSide(r.data?.data || []);
      } else {
        // Server đếm gom chính xác toàn bộ theo mẫu.
        const r = await api.get('/approvals/template-buckets', { params: { filter: filterKey } });
        bucket = (r.data?.data || [])
          .map(b => ({ id: b.id, name: b.name || i18n._(APPROVAL_OTHER_TEMPLATE), count: b.count || 0 }))
          .sort((a, b) => b.count - a.count);
      }
      setTemplateBuckets(prev => ({ ...prev, [filterKey]: bucket }));
    } catch { /* ignore */ }
  }, [templateBuckets]);

  // KHÔNG reset bucket trên counts change — gây wipe ngay sau khi loadBucketFor xong
  // (vì loadCounts cũng được trigger cùng lúc bởi URL change). Bucket cache trong session,
  // refresh khi user collapse → expand lại nếu cần.

  const updateUrl = (params) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([k, v]) => {
      if (v == null || v === '') next.delete(k); else next.set(k, v);
    });
    stripDefaultApprovalParams(next); // ?tab=tasks&filter=pending&view=table → ?
    setSearchParams(next, { replace: true });
  };

  // Build URL gọn — strip default values trước khi navigate
  const buildInboxUrl = (overrides = {}) => {
    const p = new URLSearchParams();
    Object.entries(overrides).forEach(([k, v]) => { if (v) p.set(k, v); });
    stripDefaultApprovalParams(p);
    const q = p.toString();
    return '/approvals' + (q ? `?${q}` : '');
  };

  // BUG-05 (TC-365): đổi mục/tab/template phải RESET tìm kiếm — nếu không, q cũ giữ
  // trong URL + ô search → mục mới bị filter theo từ khoá cũ → user tưởng mục trống.
  const handleSetTopTab = (tab) => {
    if (tab === topTab) return;
    const newFilter = tab === 'sent' ? 'active' : 'pending';
    setSearchText('');
    if (!isInbox) navigate(buildInboxUrl({ tab, filter: newFilter }));
    else updateUrl({ tab, filter: newFilter, q: null });
  };

  const handleSetFilter = (f) => {
    setSearchText('');
    if (!isInbox) navigate(buildInboxUrl({ tab: topTab, filter: f }));
    else updateUrl({ filter: f, id: null, template_id: null, q: null }); // đổi filter clear template + search
  };

  const handleSetTemplateFilter = (f, templateId) => {
    setSearchText('');
    if (!isInbox) navigate(buildInboxUrl({ tab: topTab, filter: f, template_id: templateId }));
    else updateUrl({ filter: f, template_id: templateId, id: null, q: null });
  };

  const handleSearchChange = (v) => {
    setSearchText(v);
    if (isInbox) updateUrl({ q: v || null });
  };

  const hasPerm = (mod) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin' || currentUser.role === 'manager') return true;
    if (currentUser.permissions?.includes('*')) return true;
    return (currentUser.permissions || []).some(p => typeof p === 'string' && p.startsWith(mod + ':'));
  };

  return (
    <div className="h-full bg-white border-r border-slate-200 w-[240px] flex flex-col">
      {/* Top tabs */}
      <div className="p-3">
        <div className="flex bg-gray-100 rounded-xl p-1">
          {TOP_TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = isInbox && topTab === tab.key;
            return (
              <button key={tab.key} onClick={() => handleSetTopTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition ${
                  isActive ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}>
                <Icon size={13} /> {i18n._(tab.label)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="px-3">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={searchText} onChange={e => handleSearchChange(e.target.value)}
            placeholder={t`Tìm kiếm...`}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-1 focus:ring-blue-500 focus:outline-none" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-3">
        {/* Filter list */}
        <div className="px-2">
          <p className="px-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
            {i18n._(topTab === 'tasks' ? APPROVAL_TAB_LABELS.tasks : APPROVAL_TAB_LABELS.sent)}
          </p>
          <nav className="space-y-0.5">
            {sidebarFilters.map(f => {
              const FIcon = f.icon;
              const isActive = isInbox && filter === f.key;
              const count = counts[f.key];
              const isExpanded = expandedFilter === f.key;
              const bucket = templateBuckets[f.key] || [];
              // Click filter row: vừa set filter vừa toggle expand cùng lúc → 1 click, đơn giản.
              // Click filter khác → set filter mới + close expand cũ + open expand mới.
              // Collapse cùng filter → invalidate bucket để re-expand sẽ fetch fresh count.
              const handleClick = () => {
                handleSetFilter(f.key);
                if (isExpanded) {
                  setExpandedFilter(null);
                  setTemplateBuckets(prev => {
                    const { [f.key]: _drop, ...rest } = prev;
                    return rest;
                  });
                } else {
                  setExpandedFilter(f.key);
                  loadBucketFor(f.key);
                }
              };
              return (
                <div key={f.key}>
                  <button type="button" onClick={handleClick}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition ${
                      isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}>
                    <FIcon size={14} className="shrink-0" />
                    <span className="flex-1 text-left">{i18n._(f.label)}</span>
                    {!f.hideCount && count != null && count > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-md font-semibold ${isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{fmtBadge(count)}</span>
                    )}
                    <ChevronRight size={13} className={`shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''} ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                  </button>
                  {isExpanded && (
                    <div className="ml-4 pl-3 border-l border-gray-200 mt-0.5 mb-1 space-y-0.5">
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleSetFilter(f.key); }}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                          isActive && !activeTemplateId ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                        }`}>
                        <span className="flex-1 text-left italic"><Trans>Tất cả mẫu</Trans></span>
                      </button>
                      {bucket.length === 0 && (
                        <p className="px-3 py-1.5 text-[11px] text-gray-400 italic"><Trans>Chưa có đơn</Trans></p>
                      )}
                      {bucket.map(tpl => {
                        const isTplActive = isActive && activeTemplateId === tpl.id;
                        return (
                          <button type="button" key={tpl.id}
                            onClick={(e) => { e.stopPropagation(); handleSetTemplateFilter(f.key, tpl.id); }}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                              isTplActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                            }`}
                            title={tpl.name}>
                            <span className="flex-1 text-left truncate">{tpl.name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0 ${isTplActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{fmtBadge(tpl.count)}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Group: Chia sẻ — kế toán gửi loạt đơn cho CEO duyệt nhanh trong 1 bảng */}
        <div className="px-2 mt-5">
          <p className="px-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5"><Trans>Chia sẻ</Trans></p>
          <nav className="space-y-0.5">
            <button onClick={() => navigate('/approvals/shared?type=received')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition ${
                isShared && sharedType === 'received' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
              }`}>
              <InboxIcon size={14} className="shrink-0" />
              <span className="flex-1 text-left"><Trans>Chia sẻ với tôi</Trans></span>
              {sharedReceivedCount > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-md font-semibold ${
                  isShared && sharedType === 'received' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                }`}>{fmtBadge(sharedReceivedCount)}</span>
              )}
            </button>
            <button onClick={() => navigate('/approvals/shared?type=sent')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition ${
                isShared && sharedType === 'sent' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
              }`}>
              <Share2 size={14} className="shrink-0" />
              <span className="flex-1 text-left"><Trans>Chia sẻ tôi gửi</Trans></span>
            </button>
          </nav>
        </div>

        {/* Group menu — Quản lý */}
        {(hasPerm('approval_templates') || hasPerm('approval_reports')) && (
          <div className="px-2 mt-5">
            <p className="px-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5"><Trans context="tiêu đề nhóm menu">Quản lý</Trans></p>
            <nav className="space-y-0.5">
              {hasPerm('approval_templates') && (
                <button onClick={() => navigate('/approvals/templates')}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition ${
                    isTemplates ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                  }`}>
                  <FileSpreadsheet size={14} className="shrink-0" />
                  <span className="flex-1 text-left"><Trans>Mẫu phê duyệt</Trans></span>
                </button>
              )}
              {hasPerm('approval_reports') && (
                <button onClick={() => navigate('/approvals/report')}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition ${
                    isReport ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                  }`}>
                  <BarChart3 size={14} className="shrink-0" />
                  <span className="flex-1 text-left"><Trans>Báo cáo</Trans></span>
                </button>
              )}
            </nav>
          </div>
        )}
      </div>

      {/* Bottom: Gửi đơn mới */}
      <div className="p-3 border-t border-gray-100">
        <button onClick={() => setShowSubmit(true)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-medium shadow-sm">
          <Plus size={14} /> <Trans>Gửi đơn mới</Trans>
        </button>
      </div>

      <ApprovalTemplatePicker open={showSubmit} onClose={() => { setShowSubmit(false); setDeepLink(null); }}
        initialTemplateId={deepLink?.templateId} initialPrefill={deepLink?.prefill}
        onSubmitted={(id) => {
          // Sau khi submit → đưa về inbox tab "Đơn của tôi → Đang xử lý" + mở popup đơn mới
          // → đồng nhất UI với view approver (drawer trên list view), user thấy đơn cùng các đơn khác
          // tab=sent + filter=active là default cho sent → strip → URL gọn /approvals?id=xxx
          if (id) navigate(buildInboxUrl({ tab: 'sent', filter: 'active', id: id.slice(0, 8) }));
        }} />
    </div>
  );
}
